'use client';

import Image from 'next/image';
import Link from 'next/link';
import { MouseEventHandler, useCallback, useRef } from 'react';
import { Favourities } from '@/components/addToFavourites/addToFavourities';
import CustomRatingStarIcon from '@/components/icons/customRatingStarIcon';
import { IconButton } from '@/components/ui/iconButton';
import { CloseIcon } from '@/components/ui/icons';
import { ProductPrice } from '@/features/products/components/productPrice/productPrice';
import { showProductName } from '@/features/products/data/utils';
import { ProductListingItem } from '@/features/products/types/product';
import { ProductNameType } from '@/features/products/types/productNames';

export default function ProductLocationCard({
  product,
  onClose
}: {
  product: ProductListingItem;
  onClose: () => void;
}) {
  const popupRef = useRef<HTMLAnchorElement | null>(null);
  const cardImg = product.images?.length
    ? product?.images?.[0]?.path || product?.images?.[0]?.fallbackPath
    : '/notavailable.jpg';
  const cardImgAlt = product.images?.length
    ? product?.images?.[0]?.alt
    : showProductName(product, ProductNameType.Listing) || '';

  const handleCloseEvent = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (onClose) {
        onClose();
      }
    },
    [onClose]
  );
  return (
    <Link
      ref={popupRef}
      href={`/pl/o/${product.slug}`}
      className="block bg-(--mui-palette-background-paper) p-3 shadow-2xl rounded-tr-lg w-max h-max no-underline transition-colors pointer-events-auto"
    >
      <div className="flex flex-col gap-2 w-43.5">
        <div className="relative size-43.5 shrink-0">
          <Image
            src={cardImg}
            alt={cardImgAlt}
            width={174}
            height={174}
            className="object-cover rounded-md w-full h-full aspect-square relative z-0"
            loading="lazy"
          />
          <div className="absolute top-1.5 left-1.5 z-20">
            <IconButton
              className={`flex align-bottom cursor-pointer bg-white hover:bg-gray-100 transition-colors duration-200`}
              aria-label="close card"
              onClick={handleCloseEvent}
            >
              <CloseIcon className="text-(--mui-palette-primary-main)" />
            </IconButton>
          </div>
          <div className="absolute top-1.5 right-1.5 z-20">
            <Favourities product={product} withBackground />
          </div>
        </div>

        <div className="flex w-full mt-1 relative z-0">
          <div className="flex flex-col w-full">
            <div className="flex gap-1 items-center">
              <CustomRatingStarIcon className="text-amber-500 size-4.5" />
              <p className="text-(--mui-palette-text-secondary)">{product?.averageRating || 0}</p>
              <p className="text-(--mui-palette-text-disabled)">({product?.reviewsCount || 0})</p>
            </div>

            <div className="flex w-full items-start mt-0.5">
              <p className="line-clamp-2 font-medium leading-tight text-base text-(--mui-palette-text-primary)">
                {showProductName(product, ProductNameType.Listing)}
              </p>
            </div>
          </div>
        </div>
        <ProductPrice
          promotion={!!product?.promotion}
          lowestPriceBeforeDiscount={product.variant.lowestPriceBeforeDiscount}
          price={product.variant.price}
          originalPrice={product.variant.originalPrice}
          priceClassNames="m-0 mt-1"
        />
      </div>
    </Link>
  );
}
